import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import showsApi from '../../client/shows-api'
import { MediaGridItem } from '../../types/Media'
import MediaGridPage from '../../areas/layout/media-grid-page'

type ShowCategory =
  | 'popular'
  | 'top-rated'
  | 'airing-today'
  | 'on-the-air'
  | 'marvel'
  | 'netflix'
  | 'network-hbo'
  | 'network-apple'
  | 'network-abc'
  | 'network-fx'
  | 'network-prime-video'
  | 'network-hulu'
  | 'network-espn'
  | 'provider-disney'

const ShowsPage = () => {
  const { category } = useParams<{ category: ShowCategory }>()
  const currentCategory: ShowCategory = category || 'popular'

  const [items, setItems] = useState<MediaGridItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const fetchShows = async (p: number) => {
    switch (currentCategory) {
      case 'popular':
        return showsApi.getPopular(p)
      case 'top-rated':
        return showsApi.getTopRated(p)
      case 'airing-today':
        return showsApi.getAiringToday(p)
      case 'on-the-air':
        return showsApi.getOnTheAir(p)
      case 'marvel':
        return showsApi.getMarvel(p)
      case 'netflix':
        return showsApi.getNetflix(p)
      case 'network-hbo':
        return showsApi.getByNetwork('hbo', p)
      case 'network-apple':
        return showsApi.getByNetwork('apple', p)
      case 'network-abc':
        return showsApi.getByNetwork('abc', p)
      case 'network-fx':
        return showsApi.getByNetwork('fx', p)
      case 'network-prime-video':
        return showsApi.getByNetwork('prime-video', p)
      case 'network-hulu':
        return showsApi.getByNetwork('hulu', p)
      case 'network-espn':
        return showsApi.getByNetwork('espn', p)
      case 'provider-disney':
        return showsApi.getByProvider('disney', p)
      default:
        return showsApi.getPopular(p)
    }
  }

  const loadPage = async (p: number) => {
    try {
      setLoading(true)

      const res = await fetchShows(p)
      const data = res.data

      if (!data || !data.results || data.results.length === 0) {
        setHasMore(false)
        return
      }

      setItems((prev) => [
        ...prev,
        ...data.results.map((s: any) => ({
          id: s.id,
          type: 'show' as const,
          title: s.title,
          year: s.year,
          rating: s.rating,
          posterUrl: s.posterUrl,
          genres: s.genres,
        })),
      ])

      setHasMore(p < data.totalPages)
    } catch {
      setError('Failed to load shows')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setItems([])
    setPage(1)
    setHasMore(true)
    loadPage(1)
  }, [currentCategory]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (loading || !hasMore) return
    const next = page + 1
    setPage(next)
    loadPage(next)
  }

  const titleText = 'Shows'

  const subtitleText =
    currentCategory === 'popular'
      ? 'Popular shows'
      : currentCategory === 'top-rated'
      ? 'Top rated shows'
      : currentCategory === 'airing-today'
      ? 'Airing today'
      : currentCategory === 'on-the-air'
      ? 'Currently airing'
      : currentCategory === 'marvel'
      ? 'Marvel Shows'
      : currentCategory === 'netflix'
      ? 'Netflix Shows'
      : currentCategory === 'network-hbo'
      ? 'HBO Shows'
      : currentCategory === 'network-apple'
      ? 'Apple Shows'
      : currentCategory === 'network-abc'
      ? 'ABC Shows'
      : currentCategory === 'network-fx'
      ? 'FX Shows'
      : currentCategory === 'network-prime-video'
      ? 'Prime Video Shows'
      : currentCategory === 'network-hulu'
      ? 'Hulu Shows'
      : currentCategory === 'network-espn'
      ? 'ESPN Shows'
      : currentCategory === 'provider-disney'
      ? 'Disney+ Shows'
      : ''

  return (
    <MediaGridPage
      title={titleText}
      subtitle={subtitleText}
      items={items}
      loading={loading}
      error={error}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
    />
  )
}

export default ShowsPage
